/**
 * Das Zählerdetail (Spec 46a „Zählerdetail", 46b „Zählerdetail — Änderungen"):
 * Kopf mit „Stand erfassen" und ⋯-Menü, Karte „Letzter Stand" (Taste zum
 * Erfassen), Liefervertrag samt früheren Verträgen und Dokumenten (Spec 46c),
 * Stammdaten und Verlauf mit Tausch-Zeilen, Belegfoto und Löschen.
 * Die Abfrage besitzt `routes/Energie.tsx`; Labels, Einheiten, `age_display`,
 * `status_display`, Quellen und `archived_on_display` kommen vom Server und
 * werden wörtlich gezeigt.
 *
 * Stilllegen und Löschen fragen zurück (`AlertDialog`). Wer die Aktionen sehen
 * darf, entscheidet der Server (Spec 46b, Nachtrag H-03): `can_edit` (nicht
 * archiviert, Rolle ab `editor`) zeigt Erfassen, Tausch und Bearbeiten,
 * `can_manage` (Rolle ab `manager`) Stilllegen und Wieder in Betrieb nehmen;
 * ohne beide entfällt das ⋯-Menü. Die Vertragsaktionen (Spec 46c) hängen an
 * `can_edit_supply` (Rolle ab `editor` am Zähler, nur intern, nicht stillgelegt), nicht an `can_edit`. Ein 403 steht weiter als Meldung am Ort
 * der Aktion (Rolle gewechselt, Seite veraltet).
 */

import { Link } from '@tanstack/react-router'
import { Image as ImageIcon, MoreHorizontal } from 'lucide-react'
import { useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'

import { PopoverMenu } from '@/components/PopoverMenu'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useArchiveMeter,
  useDeleteReading,
  useMeterPhotoUrl,
  useUnarchiveMeter,
} from '@/features/energy/api'
import type { MeterDetail as MeterDetailData, MeterReading, SupplyContract } from '@/features/energy/api'
import { MeterReadingSheet } from '@/features/energy/MeterReadingSheet'
import { MeterReplaceSheet } from '@/features/energy/MeterReplaceSheet'
import { MeterEditSheet } from '@/features/energy/MeterSheet'
import { SupplyContractCard } from '@/features/energy/SupplyContractCard'
import { SupplyHistory } from '@/features/energy/SupplyHistory'
import { ApiError } from '@/lib/api'

export type MeterDetailProps =
  | { readonly state: 'loading' }
  | { readonly state: 'error'; readonly error: unknown; readonly onRetry: () => void }
  | {
      readonly state: 'ready'
      readonly meter: MeterDetailData
      /** `haus` der URL, für den Zurück-Link. */
      readonly house: string | undefined
      readonly onSaved: () => void
      /** Nach dem Stilllegen — die Route wechselt auf die Liste. */
      readonly onArchived: () => void
      /** Abschnitt *Dokumente* (`meter-documents`): die Liste gehört `features/
       * documents`, der Container reicht sie als Platz herein. */
      readonly documents?: ReactNode
      /** Öffnen die Blätter des Liefervertrags — sie gehören dem Container. */
      readonly onSupplyAdd?: () => void
      readonly onSupplyEdit?: (contract: SupplyContract) => void
    }

export function MeterDetail(props: MeterDetailProps) {
  if (props.state === 'loading') {
    return (
      <div data-testid="meter-detail">
        <SkeletonRows testId="meter-detail-loading" rows={4} />
      </div>
    )
  }

  if (props.state === 'error') {
    return (
      <div data-testid="meter-detail">
        <ErrorState testId="meter-detail-error" retryTestId="meter-detail-retry" error={props.error} onRetry={props.onRetry} />
      </div>
    )
  }

  return (
    <MeterDetailBody
      meter={props.meter}
      house={props.house}
      onSaved={props.onSaved}
      onArchived={props.onArchived}
      documents={props.documents}
      onSupplyAdd={props.onSupplyAdd ?? NO_OP}
      onSupplyEdit={props.onSupplyEdit ?? NO_OP}
    />
  )
}

const NO_OP = (): void => undefined

function SectionTitle({ children }: { readonly children: string }) {
  return <h3 className="text-label text-muted font-medium">{children}</h3>
}

function Fact({ name, label, value }: { readonly name: string; readonly label: string; readonly value: string | null }) {
  return (
    <div className="border-border flex min-h-(--row-h) items-center justify-between gap-4 border-b px-4 last:border-b-0">
      <dt className="text-label text-muted shrink-0">{label}</dt>
      <dd data-testid={`meter-fact-${name}`} className="text-body text-text min-w-0 truncate text-right">
        {value ?? '—'}
      </dd>
    </div>
  )
}

function messageOf(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.'
}

const MENU_ITEM_CLASS =
  'text-body text-text hover:bg-bg flex min-h-(--tap-min) w-full items-center px-3 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)'

/** Die Alert-Dialoge skalieren nicht (`design-system`), wie `ArchiveControls`. */
const NO_SCALE = { '--tw-enter-scale': 1, '--tw-exit-scale': 1 } as CSSProperties

function MeterDetailBody({
  meter,
  house,
  onSaved,
  onArchived,
  documents,
  onSupplyAdd,
  onSupplyEdit,
}: {
  readonly meter: MeterDetailData
  readonly house: string | undefined
  readonly onSaved: () => void
  readonly onArchived: () => void
  readonly documents: ReactNode
  readonly onSupplyAdd: () => void
  readonly onSupplyEdit: (contract: SupplyContract) => void
}) {
  const archiveMutation = useArchiveMeter()
  const unarchiveMutation = useUnarchiveMeter()
  const deleteMutation = useDeleteReading()
  const photoUrlMutation = useMeterPhotoUrl()
  // Das Bearbeiten-Blatt gibt den Fokus an diesen Knopf zurück; er liegt im Menü
  // und wird beim Öffnen aus dem Menü gesetzt.
  const editTriggerRef = useRef<HTMLButtonElement | null>(null)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isCaptureOpen, setIsCaptureOpen] = useState(false)
  const [isReplaceOpen, setIsReplaceOpen] = useState(false)
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<MeterReading | null>(null)
  const [actionError, setActionError] = useState<string | undefined>(undefined)
  const [readingsError, setReadingsError] = useState<string | undefined>(undefined)
  const last = meter.last_reading
  const isArchived = meter.archived_at !== null
  const { can_edit: canEdit, can_manage: canManage, can_edit_supply: canEditSupply } = meter

  async function archive() {
    setActionError(undefined)
    try {
      await archiveMutation.mutateAsync({ meterId: meter.id, ifMatch: String(meter.version) })
      setIsArchiveOpen(false)
      onArchived()
    } catch (error) {
      setIsArchiveOpen(false)
      setActionError(messageOf(error))
    }
  }

  async function unarchive() {
    setActionError(undefined)
    try {
      await unarchiveMutation.mutateAsync({ meterId: meter.id, ifMatch: String(meter.version) })
    } catch (error) {
      setActionError(messageOf(error))
    }
  }

  async function deleteReading(reading: MeterReading) {
    setReadingsError(undefined)
    try {
      await deleteMutation.mutateAsync({ meterId: meter.id, readingId: reading.id })
    } catch (error) {
      setReadingsError(messageOf(error))
    } finally {
      setDeleteTarget(null)
    }
  }

  async function openPhoto(documentId: string) {
    setReadingsError(undefined)
    try {
      const signed = await photoUrlMutation.mutateAsync(documentId)
      window.open(signed.download_url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      setReadingsError(messageOf(error))
    }
  }

  function openFromMenu(open: () => void) {
    editTriggerRef.current = document.querySelector<HTMLButtonElement>('[data-testid="meter-menu"]')
    open()
  }

  const cardBody =
    last === null ? (
      <>
        <span className="text-label text-muted block">Letzter Stand</span>
        <span data-testid="meter-last-reading-empty" className="text-body text-muted block">
          Noch kein Stand
        </span>
        {canEdit && (
          <span data-testid="meter-capture-open-empty" className="text-body text-accent block">
            Ersten Stand erfassen
          </span>
        )}
      </>
    ) : (
      <>
        <span className="text-label text-muted block">
          Letzter Stand{meter.age_display !== null && ` · ${meter.age_display}`}
        </span>
        <span data-testid="meter-last-reading-value" data-numeric className="text-page font-(--font-weight-page) text-text block">
          {last.value_display} <span className="text-body text-muted font-normal">{meter.unit}</span>
        </span>
        {meter.is_reading_due && (
          <span data-testid="meter-last-reading-due" className="text-label text-status-partial-text flex items-center gap-1.5">
            <span aria-hidden="true" className="bg-status-partial size-1.5 shrink-0 rounded-full" />
            {meter.status_display}
          </span>
        )}
      </>
    )

  return (
    <article data-testid="meter-detail" className="flex flex-col gap-5 px-(--page-x) py-4">
      {/* Unter 1024 px ersetzt das Detail die Liste (Spec 46a, Web). */}
      <Link
        to="/energie"
        search={{ haus: house, zaehler: undefined }}
        data-testid="meter-detail-back"
        className="text-accent text-label flex min-h-(--tap-min) items-center self-start hover:underline lg:hidden"
      >
        Alle Zähler
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-48">
          <h2 data-testid="meter-detail-title" className="text-section font-(--font-weight-section) text-text truncate">
            {meter.label}
          </h2>
          <p data-testid="meter-detail-sub" className="text-label text-muted">
            {meter.house !== null && `${meter.house.label} · `}Nr. <span data-numeric>{meter.serial}</span>
          </p>
        </div>
        {!isArchived && (canEdit || canManage) && (
          <div className="flex shrink-0 items-center gap-2 max-sm:w-full max-sm:justify-end">
            <PopoverMenu
              triggerTestId="meter-menu"
              triggerLabel="Mehr"
              triggerClassName="border-border text-muted hover:text-text rounded-card flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center border transition-colors duration-(--duration-fast) ease-(--ease-out)"
              menuClassName="border-border bg-surface rounded-card absolute right-0 z-50 mt-1 w-56 border"
              trigger={<MoreHorizontal aria-hidden="true" className="size-4" />}
            >
              {(close) => (
                <>
                  {canEdit && (
                    <>
                      <button
                        type="button"
                        role="menuitem"
                        data-testid="meter-edit"
                        onClick={() => {
                          close()
                          openFromMenu(() => {
                            setIsEditOpen(true)
                          })
                        }}
                        className={MENU_ITEM_CLASS}
                      >
                        Bearbeiten
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        data-testid="meter-replace"
                        onClick={() => {
                          close()
                          setIsReplaceOpen(true)
                        }}
                        className={MENU_ITEM_CLASS}
                      >
                        Zähler wurde getauscht
                      </button>
                    </>
                  )}
                  {canManage && (
                    <button
                      type="button"
                      role="menuitem"
                      data-testid="meter-archive"
                      onClick={() => {
                        close()
                        setIsArchiveOpen(true)
                      }}
                      className={`${MENU_ITEM_CLASS} !text-status-overdue`}
                    >
                      Zähler stilllegen
                    </button>
                  )}
                </>
              )}
            </PopoverMenu>
            {canEdit && (
              <button
                type="button"
                data-testid="meter-capture-open"
                onClick={() => {
                  setIsCaptureOpen(true)
                }}
                className="bg-accent rounded-card text-body min-h-(--tap-min) px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
              >
                Stand erfassen
              </button>
            )}
          </div>
        )}
      </header>

      {isArchived && (
        <p
          data-testid="meter-archived-banner"
          role="status"
          className="border-border bg-bg text-text rounded-card flex flex-wrap items-center justify-between gap-2 border px-4 py-3"
        >
          <span>Stillgelegt am {meter.archived_on_display}</span>
          {canManage && (
            <button
              type="button"
              data-testid="meter-unarchive"
              disabled={unarchiveMutation.isPending}
              aria-busy={unarchiveMutation.isPending}
              onClick={() => void unarchive()}
              className="bg-accent rounded-card text-body min-h-(--tap-min) px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
            >
              Wieder in Betrieb nehmen
            </button>
          )}
        </p>
      )}

      {actionError !== undefined && (
        <p data-testid="meter-action-error" role="alert" className="text-label text-status-overdue">
          {actionError}
        </p>
      )}

      {!canEdit ? (
        <section
          data-testid="meter-last-reading-card"
          className="border-border bg-surface rounded-card flex flex-col gap-1 border p-4"
        >
          {cardBody}
        </section>
      ) : (
        <button
          type="button"
          data-testid="meter-last-reading-card"
          aria-label="Stand erfassen"
          onClick={() => {
            setIsCaptureOpen(true)
          }}
          className="border-border bg-surface hover:bg-bg rounded-card flex flex-col items-stretch gap-1 border p-4 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
        >
          {cardBody}
        </button>
      )}

      <SupplyContractCard
        contract={meter.supply_contracts.find((contract) => contract.is_current) ?? null}
        canEdit={canEditSupply}
        onEdit={onSupplyEdit}
        onAdd={onSupplyAdd}
      />
      <SupplyHistory
        contracts={meter.supply_contracts.filter((contract) => !contract.is_current)}
        canEdit={canEditSupply}
        onEdit={onSupplyEdit}
      />

      {documents !== undefined && (
        <section data-testid="meter-documents" className="flex flex-col gap-2">
          <SectionTitle>Dokumente</SectionTitle>
          <div className="border-border bg-surface rounded-card border">{documents}</div>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <SectionTitle>Stammdaten</SectionTitle>
        <dl data-testid="meter-detail-facts" className="border-border bg-surface rounded-card border">
          <Fact name="kind" label="Art" value={`${meter.kind.label} · ${meter.unit}`} />
          <Fact name="serial" label="Zählernummer" value={meter.serial} />
          <Fact name="purpose" label="Zweck" value={meter.purpose} />
          <Fact name="digits" label="Stellenzahl" value={String(meter.digits)} />
          <Fact name="tariff" label="Tarif" value={meter.tariff.label} />
          <Fact name="operator" label="Messstellenbetreiber" value={meter.operator} />
          <Fact name="interval" label="Ablesefrist" value={`${String(meter.reading_interval_days)} Tage`} />
        </dl>
      </section>

      <section data-testid="meter-readings" className="flex flex-col gap-2">
        <SectionTitle>Verlauf</SectionTitle>
        {readingsError !== undefined && (
          <p data-testid="meter-readings-error" role="alert" className="text-label text-status-overdue">
            {readingsError}
          </p>
        )}
        {meter.readings.length === 0 ? (
          <p data-testid="meter-readings-empty" className="text-body text-muted">
            Noch keine Stände.
          </p>
        ) : (
          <ul className="border-border bg-surface rounded-card border">
            {meter.readings.map((reading) => (
              <ReadingRow
                key={reading.id}
                reading={reading}
                unit={meter.unit}
                canDelete={canEdit && reading.source.value !== 'replacement'}
                reservesPhotoColumn={meter.readings.some((candidate) => candidate.photo_document_id !== null)}
                onDelete={() => {
                  setDeleteTarget(reading)
                }}
                onOpenPhoto={(documentId) => void openPhoto(documentId)}
              />
            ))}
          </ul>
        )}
      </section>

      <MeterEditSheet
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        meter={meter}
        triggerRef={editTriggerRef}
        onSaved={onSaved}
      />
      <MeterReadingSheet open={isCaptureOpen} onOpenChange={setIsCaptureOpen} meter={meter} />
      <MeterReplaceSheet open={isReplaceOpen} onOpenChange={setIsReplaceOpen} meter={meter} />

      <AlertDialog open={isArchiveOpen} onOpenChange={setIsArchiveOpen}>
        <AlertDialogContent style={NO_SCALE}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-section font-(--font-weight-section) text-text">Zähler stilllegen?</AlertDialogTitle>
            <AlertDialogDescription className="text-label text-muted">
              Der Zähler verschwindet aus der Liste. Stände und Fotos bleiben lesbar; die Nummer wird frei.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="meter-archive-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              data-testid="meter-archive-confirm"
              disabled={archiveMutation.isPending}
              onClick={(event) => {
                // Radix schließt sonst vor der Antwort; Fehler brauchen den Dialog nicht mehr.
                event.preventDefault()
                void archive()
              }}
            >
              Stilllegen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent style={NO_SCALE}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-section font-(--font-weight-section) text-text">Stand löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-label text-muted">
              Der Stand vom {deleteTarget?.read_on_display} wird endgültig gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="meter-reading-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              data-testid="meter-reading-delete-confirm"
              disabled={deleteMutation.isPending}
              onClick={(event) => {
                event.preventDefault()
                if (deleteTarget !== null) void deleteReading(deleteTarget)
              }}
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </article>
  )
}

interface ReadingRowProps {
  readonly reading: MeterReading
  readonly unit: string
  /** Anzeigeentscheidung: der Anfangsstand eines Tauschs hat keine Aktion
   * (Spec 46b, Regel 8); der Server lehnt das Löschen ohnehin ab. */
  readonly canDelete: boolean
  /** Hat eine Zeile der Liste ein Foto, halten alle Zeilen die Spalte frei —
   * sonst springen die Datumsangaben. */
  readonly reservesPhotoColumn: boolean
  readonly onDelete: () => void
  readonly onOpenPhoto: (documentId: string) => void
}

/** Eine Verlaufszeile: [Foto] Datum · Quelle … Löschen · Wert; beim Tausch
 * darunter „alt <Nummer> · Endstand <Wert> <Einheit>". Am Zeiger steht
 * „Löschen" in der ersten Zeile (sichtbar bei Hover/Fokus), unter 768 px in
 * einer zweiten, dauerhaft sichtbar — die Zeile bleibt lesbar und das Ziel
 * 44 px hoch. */
function ReadingRow({ reading, unit, canDelete, reservesPhotoColumn, onDelete, onOpenPhoto }: ReadingRowProps) {
  const sourceLabel = reading.source.value === 'manual' ? null : reading.source.label
  const photoDocumentId = reading.photo_document_id

  return (
    <li
      data-testid={`meter-reading-row-${reading.id}`}
      className="border-border group grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center border-b px-4 last:border-b-0 max-md:grid-cols-[auto_minmax(0,1fr)_auto]"
    >
      {photoDocumentId !== null ? (
        <button
          type="button"
          data-testid={`meter-reading-photo-${reading.id}`}
          aria-label="Foto ansehen"
          onClick={() => {
            onOpenPhoto(photoDocumentId)
          }}
          className="text-muted hover:text-text col-start-1 row-start-1 -ml-3 flex min-h-(--tap-min) min-w-(--tap-min) shrink-0 items-center justify-center transition-colors duration-(--duration-fast) ease-(--ease-out)"
        >
          <ImageIcon aria-hidden="true" className="size-4" />
        </button>
      ) : (
        reservesPhotoColumn && <span aria-hidden="true" className="col-start-1 row-start-1 -ml-3 min-w-(--tap-min)" />
      )}
      <span className="text-body text-text col-start-2 row-start-1 flex min-h-(--row-h) min-w-0 items-center py-1 pr-3 tabular-nums">
        <span>
          {reading.read_on_display}
          {sourceLabel !== null && <span className="text-muted">{` · ${sourceLabel}`}</span>}
        </span>
      </span>
      <span
        data-numeric
        className="text-body text-text row-start-1 whitespace-nowrap md:col-start-4 max-md:col-start-3"
      >
        {reading.value_display} {unit}
      </span>
      {reading.replacement !== null && (
        <p
          data-testid={`meter-reading-replacement-${reading.id}`}
          className="text-label text-muted row-start-2 pb-2 tabular-nums col-[2/span_3] max-md:col-[2/span_1] max-md:self-center"
        >
          alt {reading.replacement.old_serial} · Endstand {reading.replacement.end_value_display} {unit}
        </p>
      )}
      {canDelete && (
        <button
          type="button"
          data-testid={`meter-reading-delete-${reading.id}`}
          onClick={onDelete}
          className="text-label text-status-overdue min-h-(--tap-min) px-1 transition-opacity duration-(--duration-fast) ease-(--ease-out) max-md:col-start-3 max-md:row-start-2 max-md:justify-self-end md:col-start-3 md:row-start-1 md:mr-3 md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100 [@media(hover:none)]:opacity-100"
        >
          Löschen
        </button>
      )}
    </li>
  )
}
